import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";
import { X, AlertCircle, Check } from "@xsolla/xui-icons";

export interface NotificationProps extends ThemeOverrideProps {
  tone?: "neutral" | "success" | "warning" | "alert";
  type?: "toast" | "inline";
  title?: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  onClose?: () => void;
  showClose?: boolean;
  testID?: string;
}

export const Notification: React.FC<NotificationProps> = ({
  tone = "neutral",
  type = "toast",
  title,
  message,
  actionLabel,
  onAction,
  onClose,
  showClose = true,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const config = theme.sizing.notification(type);

  const toneConfig = {
    neutral: {
      bg: theme.colors.background.neutral.secondary,
      content: theme.colors.content.neutral.primary,
      icon: theme.colors.content.neutral.primary,
    },
    success: {
      bg: theme.colors.background.success.secondary,
      content: theme.colors.content.success.primary,
      icon: theme.colors.content.success.primary,
    },
    warning: {
      bg: theme.colors.background.warning.secondary,
      content: theme.colors.content.warning.primary,
      icon: theme.colors.content.warning.primary,
    },
    alert: {
      bg: theme.colors.background.alert.secondary,
      content: theme.colors.content.alert.primary,
      icon: theme.colors.content.alert.primary,
    },
  }[tone];

  const renderIcon = () => {
    switch (tone) {
      case "success":
        return <Check size={config.iconSize} color={toneConfig.icon} />;
      case "warning":
      case "alert":
        return <AlertCircle size={config.iconSize} color={toneConfig.icon} />;
      default:
        return <AlertCircle size={config.iconSize} color={toneConfig.icon} />;
    }
  };

  return (
    <Box
      testID={testID}
      backgroundColor={toneConfig.bg}
      borderRadius={config.radius}
      paddingHorizontal={config.paddingHorizontal}
      paddingVertical={config.paddingVertical}
      width={config.width}
      flexDirection="row"
      alignItems={type === "inline" ? "center" : "flex-start"}
      gap={16}
    >
      <Box
        width={config.iconWrapperSize}
        height={config.iconWrapperSize}
        alignItems="center"
        justifyContent="center"
      >
        {renderIcon()}
      </Box>

      <Box
        flex={1}
        flexDirection={type === "inline" ? "row" : "column"}
        gap={type === "inline" ? 28 : config.gap}
        alignItems={type === "inline" ? "center" : "flex-start"}
      >
        <Box flex={1} gap={4}>
          {title && (
            <Text
              color={theme.colors.content.primary}
              fontSize={config.titleSize}
              fontWeight="500"
            >
              {title}
            </Text>
          )}
          {message && (
            <Text
              color={theme.colors.content.secondary}
              fontSize={config.messageSize}
            >
              {message}
            </Text>
          )}
        </Box>

        {actionLabel && (
          <Box onPress={onAction} paddingVertical={type === "inline" ? 0 : 8}>
            <Text
              color={theme.colors.content.primary}
              fontSize={16}
              fontWeight="500"
              letterSpacing={0.4}
              whiteSpace="nowrap"
            >
              {actionLabel}
            </Text>
          </Box>
        )}
      </Box>

      {showClose && (
        <Box onPress={onClose} marginTop={type === "inline" ? 0 : 2}>
          <Box
            width={24}
            height={24}
            alignItems="center"
            justifyContent="center"
          >
            <X size={18} color={theme.colors.content.primary} />
          </Box>
        </Box>
      )}
    </Box>
  );
};
